import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import ReactDOM from 'react-dom';

// A dropdown's open menu, drawn on document.body instead of inside the field or table cell that
// opened it, and positioned against that trigger.
//
// Why: the menus used to be `absolute` children of their trigger, so any ancestor that clips its
// overflow cut them off. Card does (overflow-hidden, for its rounded corners), and so do tables,
// drawers and scroll containers, so a dropdown near the bottom of a card opened into nothing. A
// portal escapes every one of them at once, wherever the field sits.
//
// Fixed positioning follows the trigger: re-measured on any scroll (capture phase, so a scrolling
// drawer or table counts too) and on resize. When there is not room below and there is more above,
// the menu opens upwards instead, so a field at the foot of the screen still shows its options.
//
// It also owns closing: a mousedown outside both the trigger and the menu, or Escape, calls
// onClose. The fields cannot do that check themselves any more, because the menu is no longer
// inside them and a click on an option would count as "outside".

interface FloatingMenuProps {
  // The element the menu hangs from (the field, or the button that opened it).
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  // 'start' lines the menu's left edge up with the trigger's; 'end' lines up the right edges,
  // which suits a menu opened from a button at the end of a table row.
  align?: 'start' | 'end';
  // Make the menu exactly as wide as the trigger, as a select's options should be.
  matchWidth?: boolean;
  className?: string;
  children: React.ReactNode;
}

// Space kept between the trigger and the menu, and between the menu and the edge of the screen.
const GAP = 4;
const EDGE = 8;

interface Position {
  top: number;
  left: number;
  width?: number;
  maxHeight: number;
}

const FloatingMenu: React.FC<FloatingMenuProps> = ({
  anchorRef,
  open,
  onClose,
  align = 'start',
  matchWidth = false,
  className = '',
  children,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const place = useCallback((): void => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (anchor == null || menu == null) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const width = matchWidth ? rect.width : menu.offsetWidth;
    // scrollHeight, not offsetHeight: once capped below, offsetHeight reports the capped size, and
    // the next re-measure would decide the side on the wrong number.
    const height = menu.scrollHeight;

    const spaceBelow = window.innerHeight - rect.bottom - EDGE - GAP;
    const spaceAbove = rect.top - EDGE - GAP;
    const openUp = height > spaceBelow && spaceAbove > spaceBelow;

    // When neither side has room for all of it (a short window), the menu takes the roomier side
    // and shrinks to fit, scrolling, rather than sliding back over the field that opened it.
    const maxHeight = Math.max(0, openUp ? spaceAbove : spaceBelow);
    const shown = Math.min(height, maxHeight);

    const top = openUp ? rect.top - GAP - shown : rect.bottom + GAP;
    const rawLeft = align === 'end' ? rect.right - width : rect.left;
    const left = Math.min(
      Math.max(EDGE, rawLeft),
      window.innerWidth - width - EDGE,
    );

    setPosition({
      top,
      left,
      width: matchWidth ? width : undefined,
      maxHeight,
    });
  }, [anchorRef, align, matchWidth]);

  // Measure before paint, so the menu never flashes in the wrong place first.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleMouseDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (
        anchorRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // The menu's own size changes too (typing in a dropdown's search box shortens the list), and
    // a menu opened upwards has to move with it or it floats away from the field.
    const resizeObserver = new ResizeObserver(() => place());
    if (menuRef.current != null) {
      resizeObserver.observe(menuRef.current);
    }

    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, place, onClose, anchorRef]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      // Above the app's drawers (z-[81]) so a dropdown inside one still opens on top. Hidden until
      // the first measurement lands, so its first frame is never in the wrong place.
      className={`fixed z-[95] overflow-y-auto ${className}`}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        width: position?.width,
        maxHeight: position?.maxHeight,
        visibility: position == null ? 'hidden' : 'visible',
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default FloatingMenu;
